import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Leaf, List, Plus } from '@phosphor-icons/react';

import { Avatar } from '../ui/Avatar';
import { paths } from '../../routes/paths';
import { useAuth } from '../../store/AuthContext';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { AppNavDrawer } from './AppNavDrawer';
import { AppNavLogout } from './AppNavLogout';
import { QuickActionsSheet } from './QuickActionsSheet';
import {
  TABLET_RAIL_ITEMS,
  getDesktopNavSections,
  getMobileMenuSections,
  isNavItemActive,
} from './navItems';
import { NavSectionList } from './NavSection';
import { StackedNavItem } from './StackedNavItem';

/** `ds/nav/sidebar-create` — $sage-solid, $radius-ds-md, altura 44. */
function CreateButton({ collapsed, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex shrink-0 items-center justify-center gap-space-2 bg-sage-700 text-sage-50 transition-colors hover:bg-sage-800 dark:bg-sage-800 dark:hover:bg-sage-700',
        collapsed
          ? // eslint-disable-next-line no-restricted-syntax -- sombra do .pen (offset/blur) com token de cor
            'h-10 w-10 rounded-ds-full shadow-[0_4px_12px] shadow-sage-glow'
          : 'h-11 w-full rounded-ds-md px-space-3 py-space-2',
      )}
      aria-label={pt.nav.create}
    >
      <Plus className="h-4 w-4 shrink-0" weight="bold" aria-hidden />
      {!collapsed ? (
        <span className="text-type-body-sm font-semibold">{pt.nav.create}</span>
      ) : null}
    </button>
  );
}

/** `ds/brand/logo-mark--sm` (32) e `--md` (36) — gradiente sage + glow. */
function LogoBadge({ size = 'sm' }) {
  const isMd = size === 'md';
  return (
    <span
      className={cn(
        // eslint-disable-next-line no-restricted-syntax -- sombra do .pen (offset/blur) com token de cor
        'flex shrink-0 items-center justify-center bg-gradient-to-br from-sage-500 to-sage-600 text-sage-50 shadow-[0_4px_12px] shadow-sage-glow',
        isMd ? 'h-9 w-9 rounded-ds-lg' : 'h-8 w-8 rounded-ds-md',
      )}
    >
      {/* eslint-disable-next-line no-restricted-syntax -- ícone 18×18 do .pen, sem token */}
      <Leaf className={isMd ? 'h-5 w-5' : 'h-[18px] w-[18px]'} aria-hidden />
    </span>
  );
}

/** `ds/nav/sidebar-user` — avatar + nome ($type-body-sm 600) + email ($type-section). */
function SidebarUser({ profile, user }) {
  const displayName = (profile?.full_name || '').trim() || user?.email || '';
  const initial = (displayName || '?').charAt(0).toUpperCase();

  return (
    <div className="flex items-center gap-space-2-5 border-t border-border pt-space-3">
      <Avatar size="sm" initials={initial} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-type-body-sm font-semibold leading-tight text-foreground">
          {displayName || '—'}
        </p>
        {user?.email ? (
          <p className="truncate text-type-chip leading-tight text-muted-foreground">
            {user.email}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Sidebar autenticada:
 * - md–lg: `ds/nav/sidebar-rail--tablet` (72px, ícones empilhados + Menu)
 * - lg+: `ds/nav/sidebar--desktop` (280px, secções rotuladas + utilizador)
 */
export function Sidebar() {
  const { profile, user } = useAuth();
  const location = useLocation();
  const [quickOpen, setQuickOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const sections = getDesktopNavSections();
  const menuSections = getMobileMenuSections();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const railUserInitial = (
    (profile?.full_name || '').trim() ||
    user?.email ||
    '?'
  )
    .charAt(0)
    .toUpperCase();

  return (
    <>
      <aside className="hidden md:block">
        {/* Rail tablet — ds/nav/sidebar-rail--tablet */}
        <nav
          data-ds-id="ds/nav/sidebar--rail"
          // eslint-disable-next-line no-restricted-syntax -- largura 72px do .pen, sem token
          className="sticky top-0 flex h-screen w-[72px] shrink-0 flex-col items-center gap-space-3 border-r border-border bg-card p-space-3 lg:hidden"
          aria-label="Navegação principal"
        >
          <NavLink
            to={paths.dashboard}
            end
            className="flex h-10 w-full shrink-0 items-center justify-center"
            aria-label={pt.app.name}
          >
            <LogoBadge size="md" />
          </NavLink>

          <CreateButton collapsed onClick={() => setQuickOpen(true)} />

          <div className="h-space-2 shrink-0" aria-hidden />

          {/* A caixa de recorte alarga até aos 72px do rail (-mx/px $space-3) para
              a label poder transbordar a pílula sem ser cortada, como no .pen. */}
          <div className="scrollbar-hidden -mx-space-3 flex min-h-0 w-[calc(100%+24px)] flex-1 flex-col gap-space-3 overflow-y-auto px-space-3">
            {TABLET_RAIL_ITEMS.map((item) => {
              const active = isNavItemActive(location.pathname, item);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  title={item.label}
                  aria-label={item.label}
                  aria-current={active ? 'page' : undefined}
                  className="flex w-full"
                >
                  <StackedNavItem icon={item.icon} label={item.label} active={active} nowrap />
                </NavLink>
              );
            })}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-expanded={menuOpen}
              aria-haspopup="dialog"
              aria-controls="tablet-nav-menu"
              className="flex w-full"
              aria-label={pt.nav.menu}
            >
              <StackedNavItem icon={List} label={pt.nav.menu} active={menuOpen} nowrap />
            </button>
          </div>

          <div className="h-px w-full shrink-0 bg-border" aria-hidden />

          <div className="flex w-full shrink-0 justify-center py-space-2">
            <Avatar size="sm" initials={railUserInitial} />
          </div>
        </nav>

        {/* Sidebar desktop — ds/nav/sidebar--desktop */}
        <nav
          data-ds-id="ds/nav/sidebar"
          className={cn(
            // eslint-disable-next-line no-restricted-syntax -- largura 280px do .pen, sem token
            'sticky top-0 hidden h-screen w-[280px] shrink-0 flex-col gap-space-1 border-r border-border bg-card p-space-4 lg:flex',
            // eslint-disable-next-line no-restricted-syntax -- sombra do .pen com token de cor
            'shadow-[0_0_24px] shadow-glass-shadow',
          )}
          aria-label="Navegação principal"
        >
          <NavLink
            to={paths.dashboard}
            end
            className="flex shrink-0 items-center gap-space-2-5 px-space-2 py-space-1"
          >
            <LogoBadge size="sm" />
            <span className="font-display text-type-title-card font-semibold tracking-tight text-foreground">
              {pt.app.name}
            </span>
          </NavLink>

          <CreateButton onClick={() => setQuickOpen(true)} />

          <div className="h-space-2 shrink-0" aria-hidden />

          <div className="scrollbar-hidden flex min-h-0 flex-1 flex-col overflow-y-auto">
            <NavSectionList sections={sections} />
          </div>

          <div className="shrink-0">
            <SidebarUser profile={profile} user={user} />
            <AppNavLogout
              compact
              className="mt-space-1 gap-space-2 rounded-ds-md px-space-3 py-space-2 text-type-body-sm font-normal"
            />
          </div>
        </nav>
      </aside>

      <QuickActionsSheet open={quickOpen} onOpenChange={setQuickOpen} />

      <AppNavDrawer
        id="tablet-nav-menu"
        open={menuOpen}
        onOpenChange={setMenuOpen}
        sections={menuSections}
      />
    </>
  );
}
